import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { methods } from "../../constants";
import { formatValue } from "../../utilities";
import { extractCountries } from "../../data/countryExplorerData";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import styleConstants from "../../styleConstants";

const getSimpleSeries = data => {
  return [
    {
      data: data
        .sort((a, b) => {
          if (a.country < b.country) {
            return -1;
          }

          if (a.country > b.country) {
            return 1;
          }

          return 0;
        })
        .map(datum => datum.value)
    }
  ];
};

const getMethodMixSeries = data =>
  methods.map(method => ({
    name: method,
    data: data
      .filter(datum => datum.method === method)
      .map(datum => datum.value),
    color: styleConstants.categoryColors.methodColors[method]
  }));

const getSectorMixSeries = data =>
  ["Public", "Private Sub", "Private Non-Sub"].map(sector => {
    const name =
      sector === "Private Sub"
        ? "Private Subsidized"
        : sector === "Private Non-Sub"
        ? "Private Non-Subsidized"
        : sector;

    return {
      name,
      data: data
        .filter(datum => datum.sector === sector)
        .map(datum => datum.value),
      color: styleConstants.categoryColors.sectorColors[name]
    };
  });

const getSeries = (data, chartType, indicator) => {
  switch (chartType) {
    case "simple":
      return getSimpleSeries(data).map(series => ({
        ...series,
        name: indicator
      }));

    case "method mix":
      return getMethodMixSeries(data, "method");

    case "sector mix":
      return getSectorMixSeries(data, "sector");

    default:
      throw new Error(`${chartType} is an invalid chart type`);
  }
};

const getSimpleChartBarColor = (method, sector) => {
  // Set default color if *both* method and sector are specified
  // if (method && sector) {
  //   return styleConstants.colors.themeBlue;
  // }

  if (method && !sector) {
    return styleConstants.categoryColors.methodColors[method];
  } else if (!method && sector) {
    const name =
      sector === "Private Sub"
        ? "Private Subsidized"
        : sector === "Private Non-Sub"
        ? "Private Non-Subsidized"
        : sector;

    return styleConstants.categoryColors.sectorColors[name];
  } else {
    return styleConstants.colors.themeBlue;
  }
};

const formatSimpleTooltip = (point, indicator, method, sector) => {
  let tooltip = "";

  if (sector) {
    tooltip = sector;
  }

  if (method) {
    if (tooltip) {
      tooltip += " ";
    }

    tooltip += method;
  }

  if (tooltip) {
    tooltip += " ";
  }

  return `${tooltip} ${indicator}, ${formatValue(point.y)}`;
};

const formatMixTooltip = (point, indicator, method, sector) => {
  const tooltip = formatSimpleTooltip(point, indicator, method, sector);

  return `${tooltip} (${formatValue(point.percentage / 100, true)})`;
};

const formatMethodMixTooltip = (point, indicator, sector) =>
  formatMixTooltip(point, indicator, point.series.name, sector);

const formatSectorMixTooltip = (point, indicator, method) =>
  formatMixTooltip(point, indicator, method, point.series.name);

const formatTooltip = (point, chartType, indicator, method, sector) => {
  switch (chartType) {
    case "simple":
      return formatSimpleTooltip(point, indicator, method, sector);

    case "method mix":
      return formatMethodMixTooltip(point, indicator, sector);

    case "sector mix":
      return formatSectorMixTooltip(point, indicator, method);

    default:
      throw new Error(`${chartType} is an invalid chart type`);
  }
};

const CountryExplorerCurrentChart = props => {
  const countries = extractCountries(props.data);

  const series = getSeries(props.data, props.chartType, props.indicator);

  const options = {
    chart: {
      type: "bar",
      height: props.height,
      backgroundColor: null,
      style: {
        fontFamily: "'Source Sans Pro', sans-serif;"
      }
    },
    title: {
      text: undefined
    },
    plotOptions: {
      bar: {
        stacking:
          series.length > 1
            ? props.absolute
              ? "normal"
              : "percent"
            : undefined,
        color:
          props.chartType === "simple"
            ? getSimpleChartBarColor(props.method, props.sector)
            : undefined,
        borderWidth: 1,
        borderColor: styleConstants.colors.gray2
      },
      series: {
        pointWidth: 20
      }
    },
    xAxis: {
      categories: Object.keys(countries),
      lineWidth: 0,
      tickmarkPlacement: "on",
      gridLineWidth: 1,
      gridLineDashStyle: "ShortDot",
      gridLineColor: styleConstants.colors.gray4,
      labels: {
        style: {
          fontSize: "1rem",
          color: styleConstants.colors.gray6
        }
      }
    },
    yAxis: {
      opposite: true,
      reversedStacks: false,
      offset: 20,
      lineWidth: 1,
      lineColor: styleConstants.colors.gray4,
      tickColor: styleConstants.colors.gray4,
      tickPixelInterval: 100,
      tickWidth: 1,
      tickPosition: "inside",
      tickLength: 5,
      gridLineWidth: 0,
      title: {
        enabled: false
      },
      labels: {
        autoRotation: false,
        style: {
          whiteSpace: "nowrap",
          fontSize: "1rem",
          color: "lightgray"
        }
      }
    },
    series: series,
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      formatter: function() {
        return formatTooltip(this, props.chartType, props.indicator);
      },
      style: {
        fontSize: "0.875rem",
        color: styleConstants.colors.gray6
      }
    },
    legend: {
      verticalAlign: "top",
      enabled: series.length > 1
    },
    exporting: {
      enabled: false
    },
    credits: {
      enabled: false
    }
  };

  return (
    <HighchartsReact
      ref={props.innerRef}
      highcharts={Highcharts}
      options={options}
    />
  );
};

CountryExplorerCurrentChart.propTypes = {
  data: PropTypes.array.isRequired,
  chartType: PropTypes.oneOf(["simple", "method mix", "sector mix"]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  indicator: PropTypes.string.isRequired,
  method: PropTypes.string,
  sector: PropTypes.string,
  absolute: PropTypes.bool
};

CountryExplorerCurrentChart.defaultProps = {
  chartType: "simple",
  absolute: true
};

export default memo(
  React.forwardRef((props, ref) => (
    <CountryExplorerCurrentChart innerRef={ref} {...props} />
  ))
);
